/**
 * Where the language preference meets the backend.
 *
 * Two reads, not one: a member who has never picked a language inherits the
 * workspace default (Settings → Workspace → Default Language), which is a
 * separate row and a separate query. `UserPreferences.language` is the user's
 * own choice and is null until they make one — the backend does not resolve the
 * fallback into that field, it resolves precedence per request for its own
 * content, so the web app has to do the same for the UI.
 *
 * Coffee PR #4553 added the two pieces this uses: a `language` field on
 * `UserPreferences` and a dedicated `updateUserLanguage(workspaceId, language)`
 * mutation. The mutation is dedicated rather than folded into the notification
 * preferences one the timezone picker uses, because `NTFPreferencesInput`
 * requires eight booleans — a language change would otherwise have to restate
 * the user's whole notification setup.
 *
 * Cache handling: both the query and the mutation select `id` on
 * `UserPreferences`, so the two results normalize to the SAME cache entity and
 * Apollo merges the new language into the cached `getUserPreferences` result by
 * itself. That is deliberately not `refetchQueries` — a refetch here would
 * re-run the preferences query behind the settings page (see CLAUDE.md).
 */
import { useCallback } from "react";

import {
  useGetUserPreferencesQuery,
  useGetWorkspacePreferencesQuery,
  useUpdateUserLanguageMutation,
} from "@/lib/graphql/types-and-hooks";
import { useWorkspaceId } from "@/lib/hooks/use-workspace-id";
import { isLovablePreview } from "@/lib/utils/environment";

import type { StoredLanguageSource } from "./storage";

export interface LanguagePersistence {
  /**
   * The language this account resolves to: the user's own if they have picked
   * one, otherwise the workspace default. Null while still unknown, and also
   * once both reads come back empty — `remoteSettled` tells the two apart.
   * Outranks localStorage: see `useLanguage`.
   */
  remoteLanguage: string | null;
  /** Which layer `remoteLanguage` came from, or null when it is null. */
  remoteSource: StoredLanguageSource | null;
  /**
   * True once the answer is known, including "nobody has set one". Until then
   * `useLanguage` leaves the language the detector picked alone rather than
   * acting on a half-loaded picture.
   */
  remoteSettled: boolean;
  /** The workspace this answer belongs to — language preferences are per-workspace. */
  workspaceId: string | null;
  /** Save a newly chosen language to the user's account. */
  persistLanguage: (code: string) => Promise<void>;
}

/**
 * Backend read/write for the language preference.
 *
 * Preview mode skips the network entirely and reports no remote language, so
 * the picker still works there off localStorage alone.
 */
export function useLanguagePersistence(): LanguagePersistence {
  const workspaceId = useWorkspaceId();
  const offline = isLovablePreview() || !workspaceId;

  const { data } = useGetUserPreferencesQuery({
    variables: { workspaceId: workspaceId ?? "" },
    skip: offline,
    // The settings page and the notification hooks read the same query; sharing
    // their cached copy is the point of not forcing a network fetch here.
    fetchPolicy: "cache-first",
  });

  // The workspace default, for members who have never picked a language. Not
  // permission-gated: `workspacePreferences` needs workspace membership, not
  // admin, and every member has to be able to resolve their own language. The
  // feature-flag hooks already run this query on every boot, so cache-first
  // makes this a cache read rather than a second request.
  const { data: workspaceData } = useGetWorkspacePreferencesQuery({
    variables: { workspaceId: workspaceId ?? "" },
    skip: offline,
    fetchPolicy: "cache-first",
  });

  const [updateUserLanguage] = useUpdateUserLanguageMutation();

  const persistLanguage = useCallback(
    async (code: string) => {
      if (offline) return;
      try {
        await updateUserLanguage({
          variables: { workspaceId: workspaceId as string, language: code },
        });
      } catch (error) {
        // The language has already been applied locally and written to
        // localStorage by the time we get here, so a failed save costs the user
        // nothing in this session — it just won't follow them to another
        // browser. Losing the whole language switch over it would be worse.
        console.error("Failed to save language preference", error);
      }
    },
    [offline, updateUserLanguage, workspaceId],
  );

  // Precedence, mirroring the backend's (user preference > workspace default >
  // Accept-Language > English) for the half of it an account can answer. The
  // user's own language settles the question on its own — there is no reason to
  // wait for the workspace read. Only when that read has come back *without* a
  // language does the workspace default get a say, which is what keeps a slow
  // workspace query from briefly overriding a user who has their own.
  const userLanguage = data?.userPreferences?.language ?? null;
  const workspaceLanguage = workspaceData?.workspacePreferences?.defaultLanguage ?? null;

  let remoteLanguage: string | null = null;
  let remoteSource: StoredLanguageSource | null = null;
  let remoteSettled = false;

  if (userLanguage) {
    remoteLanguage = userLanguage;
    remoteSource = "user";
    remoteSettled = true;
  } else if (data && workspaceData) {
    remoteLanguage = workspaceLanguage;
    remoteSource = workspaceLanguage ? "workspace" : null;
    // Settled even when both are empty: "nobody has set one" is an answer, and
    // `useLanguage` needs it to retire a workspace default that was cleared.
    remoteSettled = true;
  }

  return {
    remoteLanguage,
    remoteSource,
    remoteSettled,
    workspaceId,
    persistLanguage,
  };
}
